'use client'

/**
 * Training Arrival Popover (Issue #4147, #4179)
 *
 * When users navigate to a page from a training item link, this
 * component shows a contextual popover explaining what they're
 * seeing, auto-completes the training item, shows a celebration
 * animation, and provides a"Next Step" button to return to
 * the training hub.
 *
 * Reads the`training` query parameter to determine which item
 * triggered the navigation, fetches the item's info, marks the
 * item complete, and renders a persistent floating card.
 *
 * Issue #4179 changes:
 * - Auto-complete training item when popover appears
 * - Z-index raised above nav bar and command bar
 * - Removed"Take a look around..." text
 * - Added celebration animation on completion
 * - Added"Next Step" button navigating to /training
 *
 * Issue #4195 changes:
 * - Added dismiss button back (popover can cover UI)
 * - Right-aligned"Next Step" button
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import {
  GraduationCap,
  CheckCircle2,
  ArrowRight,
  X,
  Minus,
} from '@/components/icons'
import {
  getTrainingItemInfo,
  completeTrainingItem,
} from '@/app/actions/training-items'
import { checkTrainingItemCompleted } from '@/app/actions/training-tracking'
import { TRAINING_CONTEXT_PARAM } from '@/lib/training-utils'
import { SEMANTIC_COLORS } from '@/lib/color-constants'
import { celebrateSmall } from '@/lib/training/celebration'
import { useTrainingContext } from '@/contexts/training-context'
import {
  FloatingNotice,
  FLOATING_NOTICE_ICON_TINT,
  FLOATING_NOTICE_TILE_TINT,
} from '@/components/floating-notice'

/** Polling interval for checking action item completion */
const COMPLETION_POLL_INTERVAL_MS = 3_000

/** Stop polling after 5 minutes to avoid indefinite server requests */
const MAX_POLL_DURATION_MS = 5 * 60 * 1_000

interface ItemInfo {
  title: string
  description: string
  completionAction: string | null
  hintText: string | null
}

export function TrainingArrivalPopover() {
  // useSearchParams can return null during prerender — optional chaining
  // avoids throwing mid-render and triggering React #310 (issue #5520).
  const searchParams = useSearchParams()
  const router = useRouter()
  const { refresh } = useTrainingContext()
  const [itemInfo, setItemInfo] = useState<ItemInfo | null>(null)
  const [completed, setCompleted] = useState(false)
  const [minimized, setMinimized] = useState(false)
  // Issue #4291: Track whether user has interacted with minimized circle
  const [hasInteracted, setHasInteracted] = useState(false)
  const processedRef = useRef<string | null>(null)

  const itemId = searchParams?.get(TRAINING_CONTEXT_PARAM) ?? null

  // Issue #4291: Handler for expanding from minimized state
  const handleExpand = useCallback(() => {
    setHasInteracted(true)
    setMinimized(false)
  }, [])

  useEffect(() => {
    if (!itemId || processedRef.current === itemId) return

    processedRef.current = itemId
    setCompleted(false)
    setMinimized(false)
    setHasInteracted(false)

    async function fetchAndComplete() {
      const result = await getTrainingItemInfo(itemId as string)
      if (result.success && result.data) {
        setItemInfo(result.data)

        // Issue #4245: Only auto-complete items with page.viewed criteria.
        // Items requiring specific user actions (e.g., feedback.created,
        // pulse.completed) must NOT be auto-completed on navigation —
        // the user needs to actually perform the action.
        const isPageViewItem = result.data.completionAction === 'page.viewed'

        if (isPageViewItem) {
          const completionResult = await completeTrainingItem(itemId as string)
          if (completionResult.success) {
            setCompleted(true)
            celebrateSmall({ x: 0.5, y: 0.3 })
            await refresh()
          }
        }
      }
    }

    fetchAndComplete()
  }, [itemId, refresh])

  // Issue #4291: Poll for completion of non-page.viewed (action) items.
  // The popover previously never detected when action items were completed
  // via the training event system (e.g., feedback.created, group.viewed).
  // Stops polling when: completed, dismissed (itemInfo null), or after 5 min.
  useEffect(() => {
    if (!itemId || completed) return
    if (!itemInfo || itemInfo.completionAction === 'page.viewed') return

    const startTime = Date.now()
    const interval = setInterval(async () => {
      if (Date.now() - startTime > MAX_POLL_DURATION_MS) {
        clearInterval(interval)
        return
      }
      const result = await checkTrainingItemCompleted(itemId)
      if (result.success && result.completed) {
        setCompleted(true)
        celebrateSmall({ x: 0.5, y: 0.3 })
        await refresh()
      }
    }, COMPLETION_POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [itemId, completed, itemInfo, refresh])

  const cleanTrainingParam = () => {
    const url = new URL(window.location.href)
    url.searchParams.delete(TRAINING_CONTEXT_PARAM)
    window.history.replaceState({}, '', url.toString())
  }

  const handleDismiss = () => {
    setItemInfo(null)
    cleanTrainingParam()
  }

  const handleNextStep = () => {
    setItemInfo(null)
    cleanTrainingParam()
    router.push('/training')
  }

  if (!itemInfo) return null

  if (minimized) {
    return (
      <div
        className="fixed top-4 left-4 z-menu"
        role="status"
        aria-live="polite"
        data-testid="training-arrival-popover"
      >
        <button
          type="button"
          onClick={handleExpand}
          className={`size-10 pointer-coarse:size-11 flex items-center
  justify-center rounded-full shadow-lg
  border animate-in fade-in zoom-in duration-300
  hover:shadow-xl hover:scale-105
  ${
    completed
      ? `bg-status-green-bg-light  border-status-green-border${!hasInteracted ? ' animate-pulse' : ''}`
      : 'bg-surface-elevated border-stroke-regular'
  }`}
          aria-label="Expand training"
        >
          {completed ? (
            <CheckCircle2 className="h-5 w-5 text-status-green-text" />
          ) : (
            <GraduationCap className="h-5 w-5 text-status-blue-text" />
          )}
        </button>
      </div>
    )
  }

  return (
    <FloatingNotice
      role="status"
      aria-live="polite"
      data-testid="training-arrival-popover"
      tileClassName={
        completed ? SEMANTIC_COLORS.success.bgLight : FLOATING_NOTICE_TILE_TINT
      }
      icon={
        completed ? (
          <CheckCircle2 className="h-4 w-4 text-status-green-text animate-in zoom-in duration-300" />
        ) : (
          <GraduationCap className={`h-4 w-4 ${FLOATING_NOTICE_ICON_TINT}`} />
        )
      }
      controls={
        <>
          <button
            type="button"
            onClick={() => setMinimized(true)}
            className="flex pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px] items-center justify-center rounded-lg p-2 text-text-secondary transition-colors hover:text-text-secondary"
            aria-label="Minimize"
          >
            <Minus className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="flex pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px] items-center justify-center rounded-lg p-2 text-text-secondary transition-colors hover:text-text-secondary"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </>
      }
    >
      <h3 className="text-sm font-semibold text-text-primary">
        {itemInfo.title}
      </h3>
      <p className="text-sm text-text-secondary mt-1">{itemInfo.description}</p>
      {!completed &&
        itemInfo.hintText &&
        itemInfo.completionAction !== 'page.viewed' && (
          <p className="text-xs text-status-blue-text mt-2 italic">
            {itemInfo.hintText}
          </p>
        )}
      {completed && (
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs font-medium text-status-green-text animate-in fade-in duration-300">
            Step complete!
          </span>
          <button
            type="button"
            onClick={handleNextStep}
            className="inline-flex items-center gap-1.5 px-3 py-2.5 pointer-coarse:min-h-[44px] text-xs font-semibold text-white-static bg-action-primary hover:bg-action-primary-hover rounded-lg transition-colors animate-in fade-in slide-in-from-bottom-1 duration-300"
          >
            Next Step
            <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      )}
    </FloatingNotice>
  )
}
